"use client";

import { useMemo, useRef, useEffect } from "react";
import { NuqsAdapter } from "nuqs/adapters/next/app";
import { FeatureFlagProvider } from "@/lib/contexts/feature-flags-context";
import { ReportsProvider } from "@/lib/contexts/reports-context";
import { MessagesProvider } from "@/lib/contexts/MessagesContext";
import { ConnectionsProvider } from "@/lib/contexts/ConnectionsContext";
import { QueryProvider } from "@/lib/providers/query-provider";
import GraphQLProviders from "@/src/lib/providers";
import { ClientAccessWrapper } from "@/features/contacts/components/ClientAccessWrapper";
import {
  PreferencesProvider,
  CalendarContextProvider,
  UniversalAuthProvider,
  setAuthTokenGetter,
  DrawerStackProvider,
  GlobalModalStackProvider,
  GlobalDrawerStackProvider,
  MantineProvider,
} from "@wtc/ui";
import { createClient } from "@wtc/ui";
import { setAuthTokenGetter as setMeridianAuthTokenGetter } from "@wtc/meridian";
import { USER_ROLES, isEmployeeRole, isAdminRole, isClientRole } from "@/lib/constants/user-types";
import { initializeAllEntityActions } from "@/lib/actions";
import { HubModalHistoryProvider } from "@/lib/modals/history/HubModalHistoryProvider";

interface ClientProvidersProps {
  children: React.ReactNode;
}

export function ClientProviders({ children }: ClientProvidersProps) {
  // Ref holding current Supabase access token for sync token getter (meridian/authenticated-fetch expects () => string | null)
  const tokenRef = useRef<string | null>(null);

  // Create fresh Supabase client instance to avoid stale auth state
  // Memoize to prevent infinite re-renders
  const supabaseClient = useMemo(() => {
    const client = createClient();

    // Expose the Supabase client globally for Apollo Client to access
    if (typeof window !== "undefined") {
      (window as any).__SUPABASE_CLIENT__ = client;
    }

    return client;
  }, []);

  // Sync token into ref so meridian's setAuthTokenGetter can use a synchronous getter
  useEffect(() => {
    let mounted = true;
    void supabaseClient.auth.getSession().then(({ data: { session } }) => {
      if (mounted) tokenRef.current = session?.access_token ?? null;
    });
    const {
      data: { subscription },
    } = supabaseClient.auth.onAuthStateChange((_event, session) => {
      tokenRef.current = session?.access_token ?? null;
    });
    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [supabaseClient]);

  // Register synchronous Supabase token getter for @wtc/ui apiClient and @wtc/meridian (POST /api/meridian/messages, etc.)
  useEffect(() => {
    const syncGetter = () => tokenRef.current ?? null;
    setAuthTokenGetter(syncGetter);
    setMeridianAuthTokenGetter(syncGetter);
  }, []);

  // Initialize action registry on mount
  useEffect(() => {
    initializeAllEntityActions();
  }, []);

  return (
    <MantineProvider>
      <QueryProvider>
        <GraphQLProviders>
          <UniversalAuthProvider supabaseClient={supabaseClient}>
            <PreferencesProvider>
              <CalendarContextProvider>
                <FeatureFlagProvider>
                  <ReportsProvider>
                    <MessagesProvider>
                      <ConnectionsProvider>
                        <NuqsAdapter>
                          <GlobalModalStackProvider syncWithUrl={false}>
                            <HubModalHistoryProvider>
                              <GlobalDrawerStackProvider>
                                <DrawerStackProvider
                                  maxDepth={3}
                                  enableNesting={true}
                                  mobileOptimization={true}
                                >
                                  <ClientAccessWrapper>{children}</ClientAccessWrapper>
                                </DrawerStackProvider>
                              </GlobalDrawerStackProvider>
                            </HubModalHistoryProvider>
                          </GlobalModalStackProvider>
                        </NuqsAdapter>
                      </ConnectionsProvider>
                    </MessagesProvider>
                  </ReportsProvider>
                </FeatureFlagProvider>
              </CalendarContextProvider>
            </PreferencesProvider>
          </UniversalAuthProvider>
        </GraphQLProviders>
      </QueryProvider>
    </MantineProvider>
  );
}
